'use client';
import Image from 'next/image';
import { redirect } from 'next/navigation';
import { useState } from 'react';

import { SRMStatus } from '@scaleup/models';
import {
  useAnswerOpportunityQuestionsMutation,
  useGetOpportunityQuery,
  useUpdateOpportunityStatusMutation,
} from '../../api';
import { PLACEHOLDER_PROFILE_IMAGE } from '../../constants';
import { Button, Modal, OrgAvatar } from '../../ui';

export const OpportunityAnswerQuestionsModal = ({
  matchId,
  closeModal,
}: {
  matchId: string;
  closeModal: () => void;
}) => {
  const { data: opportunity, isLoading } = useGetOpportunityQuery(matchId);
  const [answerQuestions, { isLoading: isAnsweringQuestions }] =
    useAnswerOpportunityQuestionsMutation();
  const [optIn, { isLoading: isOptingIn }] =
    useUpdateOpportunityStatusMutation();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const questions: any[] = [];

  let initialAnswers: { [questionId: string]: string } = {};
  questions.forEach(([questionId]) => {
    initialAnswers[questionId] = '';
  });

  const [answers, setAnswers] = useState<{
    [questionId: string]: string;
  }>(initialAnswers);

  if (!opportunity) {
    return redirect('/opportunities');
  }

  const handleCloseModal = () => {
    closeModal();
  };

  const handleSubmitAnswers = async () => {
    await answerQuestions({
      id: matchId,
      answers,
    });
    await optIn({
      id: matchId,
      status: SRMStatus.ACCEPTED,
    });
    setShowConfirmation(true);
  };

  return !showConfirmation ? (
    <Modal
      title="Review and Opt-In"
      icon={'/icons/rocket.svg'}
      showModal={true}
      handleCloseModal={handleCloseModal}
    >
      <div className="flex gap-8">
        <div className="flex flex-col gap-8 flex-1">
          <div className="flex flex-col gap-2">
            <span className="text-base font-normal text-text-secondary">
              To proceed with this supplier opportunity, please answer the
              following questions from the enterprise. Your answers will help
              them determine if you are the right fit for their needs.
            </span>
          </div>
          {questions.length > 0 && (
            <div className="flex flex-col gap-4 transition ease-in-out delay-150">
              {questions.map((question, index) => (
                <div key={index} className="space-y-4">
                  <div className="flex justify-start items-end gap-2">
                    <Image
                      src={
                        opportunity.customerAvatar?.image ||
                        PLACEHOLDER_PROFILE_IMAGE
                      }
                      width={40}
                      height={40}
                      alt="Avatar"
                    />
                    <span className="bg-primary-100 rounded-2xl rounded-bl-md p-4">
                      {question[1]}
                    </span>
                  </div>
                  <div className="flex justify-end items-end gap-2">
                    <textarea
                      className="text-gray-900 rounded-2xl rounded-br-md p-4 w-96 bg-gray-100 resize-none"
                      placeholder="Enter your answer here..."
                      onChange={(e) => {
                        setAnswers({
                          ...answers,
                          [question[0]]: e.target.value,
                        });
                      }}
                      value={answers[question[0]]}
                    />
                    <OrgAvatar size={40} orgId={opportunity.orgId} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3">
            <div className="w-fit">
              <Button
                text="Submit Answers"
                variant={
                  isLoading ||
                  isAnsweringQuestions ||
                  isOptingIn ||
                  questions.length < 1 ||
                  questions.some(
                    (question) =>
                      !answers[question[0]] || answers[question[0]] === ''
                  )
                    ? 'disabled'
                    : 'primary'
                }
                onClick={handleSubmitAnswers}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  ) : (
    <Modal
      title="Opportunity Accepted"
      icon={'/icons/sparkles.svg'}
      confirmation={true}
      showCloseButton={true}
      showModal={true}
      handleCloseModal={handleCloseModal}
    >
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4 items-center">
          <span className="text-2xl font-bold text-text-primary text-center">
            Thank You for Your Response
          </span>
          <span className="text-text-secondary max-w-[640px] text-center">
            Thank you for providing the additional information. Your answers
            have been submitted successfully. The enterprise will review your
            responses and determine if you are a suitable fit for their supplier
            needs. We will notify you once a decision has been made.
          </span>
        </div>
        <Image
          src="/questions-confirmation.png"
          alt="Confirmation"
          width={1024}
          height={300}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </Modal>
  );
};
